import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { LogsType } from '../../../shared/constants/logs-type.enum';

/**
* LoggerService is responsible for logging messages with different log levels.
*
* @example
* ```typescript
* // Use this in Components and traditional Services
* constructor(private readonly logger: LoggerService)
*
* // Use this in Interceptors, Guards and Functions
  private readonly logger = inject(LoggerService);
* ```
*/
@Injectable({
  providedIn: 'root'
})

export class LoggerService {
  private completeMessage = '';

  constructor(private readonly logger: NGXLogger) { }

  /**
   * Logs a message with the specified log type, class, function, and additional context.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.logger.logMessage(LogsType.ERROR, 'ClassName', 'FunctionName', 'Resultado :', error);
   *
   * // Use this in Interceptors, Guards and Functions
   * logger.logMessage(LogsType.ERROR, 'ClassName', 'FunctionName', 'Resultado :', error);
   * ```
   *
   * @param type - The type of log (INFO, LOG, WARN, ERROR, FATAL, TRACE).
   * @param currentClass - The name of the class.
   * @param currentFunction - The name of the function.
   * @param message - The message to log.
   * @param additional - Additional parameters to log.
   */
  logMessage(type: LogsType, currentClass: string, currentFunction: string, message?: unknown, ...additional: unknown[]): void {
    if (typeof message === 'function') {
      message = message();
    }

    this.setMessage(type, currentClass, currentFunction, message);

    switch (type) {
      case LogsType.INFO:
        this.logger.info(this.completeMessage, ...additional);
        break;
      case LogsType.LOG:
        this.logger.log(this.completeMessage, ...additional);
        break;
      case LogsType.WARN:
        this.logger.warn(this.completeMessage, ...additional);
        break;
      case LogsType.ERROR:
        this.logger.error(this.completeMessage, ...additional);
        break;
      case LogsType.FATAL:
        this.logger.fatal(this.completeMessage, ...additional);
        break;
      case LogsType.TRACE:
      default:
        this.logger.trace(this.completeMessage, ...additional);
        break;
    }
  }

  /**
   * Sets the complete log message with an appropriate icon based on the log type.
   *
   * @private
   * @param {LogsType} type - The type of log message (INFO, LOG, WARN, ERROR, FATAL, TRACE).
   * @param {string} currentClass - The name of the class.
   * @param {string} currentFunction - The name of the function.
   * @param {any} [message] - The log message content.
   */
  private setMessage(type: LogsType, currentClass: string, currentFunction: string, message?: unknown): void {
    let icon = '';

    switch (type) {
      case LogsType.INFO:
        icon = '📢';
        break;
      case LogsType.LOG:
        icon = '🪲';
        break;
      case LogsType.WARN:
        icon = '⚠️';
        break;
      case LogsType.ERROR:
        icon = '❌';
        break;
      case LogsType.FATAL:
        icon = '💀';
        break;
      case LogsType.TRACE:
      default:
        icon = '🔍';
        break;
    }

    this.completeMessage = `${icon} [${currentClass}]: ${currentFunction}${message ? ` - ${message}` : ''}`;
  }
}
